import React, { useState } from 'react';
import { AlertTriangle, Shield, Zap, Power, Link2, Unlink } from 'lucide-react';
import { useExchanges, useConnectExchange, useActivateLive, useShutdownStatus, useEmergencyShutdown, useDeactivateShutdown } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner, Modal, StatCard } from '@/components/UI';
import { formatDateTime, statusColor } from '@/utils';

export function ExchangeConnectionPanel() {
  const { data: exchanges, isLoading } = useExchanges();
  const connectExchange = useConnectExchange();
  const activateLive = useActivateLive();
  const { data: shutdownStatus } = useShutdownStatus();
  const emergencyShutdown = useEmergencyShutdown();
  const deactivateShutdown = useDeactivateShutdown();

  const [showConnect, setShowConnect] = useState(false);
  const [showLiveActivation, setShowLiveActivation] = useState(false);
  const [showShutdown, setShowShutdown] = useState(false);
  const [form, setForm] = useState({
    name: '',
    exchange_type: 'binance',
    api_key: '',
    api_secret: '',
    passphrase: '',
  });
  const [liveForm, setLiveForm] = useState({
    exchange_id: '',
    confirmation_phrase: '',
    risk_acknowledgement: false,
  });
  const [shutdownReason, setShutdownReason] = useState('');

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await connectExchange.mutateAsync(form);
      setShowConnect(false);
      setForm({ name: '', exchange_type: 'binance', api_key: '', api_secret: '', passphrase: '' });
    } catch { /* handled */ }
  };

  const handleActivateLive = async (e: React.FormEvent) => {
    e.preventDefault();
    if (liveForm.confirmation_phrase !== 'I UNDERSTAND THE RISKS OF LIVE TRADING') {
      alert('Confirmation phrase does not match. Please type exactly: I UNDERSTAND THE RISKS OF LIVE TRADING');
      return;
    }
    if (!liveForm.risk_acknowledgement) {
      alert('You must acknowledge the risk warnings');
      return;
    }
    try {
      await activateLive.mutateAsync(liveForm);
      setShowLiveActivation(false);
    } catch { /* handled */ }
  };

  const handleShutdown = async () => {
    try {
      await emergencyShutdown.mutateAsync(shutdownReason || 'Manual emergency shutdown');
      setShowShutdown(false);
      setShutdownReason('');
    } catch { /* handled */ }
  };

  const handleDeactivateShutdown = async () => {
    try {
      await deactivateShutdown.mutateAsync();
    } catch { /* handled */ }
  };

  const exchangeList = exchanges ?? [];

  return (
    <div className="space-y-6">
      {/* Emergency Shutdown Status */}
      {shutdownStatus?.is_active && (
        <div className="p-4 rounded-lg bg-loss/10 border border-loss/30 glow-loss">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-loss" />
              <div>
                <p className="text-sm font-bold text-loss">EMERGENCY SHUTDOWN ACTIVE</p>
                <p className="text-xs text-surface-400">
                  All trading is blocked. Reason: {shutdownStatus.reason}
                </p>
                <p className="text-xs text-surface-500">
                  Activated: {formatDateTime(shutdownStatus.activated_at ?? '')} by {shutdownStatus.activated_by}
                </p>
              </div>
            </div>
            <button onClick={handleDeactivateShutdown} className="btn-success btn-sm">
              <Power className="w-4 h-4" /> Deactivate Shutdown
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button onClick={() => setShowConnect(true)} className="btn-primary btn-sm">
          <Link2 className="w-4 h-4" /> Connect Exchange
        </button>
        <button onClick={() => setShowLiveActivation(true)} className="btn-secondary btn-sm">
          <Zap className="w-4 h-4" /> Activate Live Mode
        </button>
        <button onClick={() => setShowShutdown(true)} className="btn-danger btn-sm">
          <Shield className="w-4 h-4" /> Emergency Shutdown
        </button>
      </div>

      {/* Exchange List */}
      {isLoading ? (
        <LoadingSpinner />
      ) : exchangeList.length === 0 ? (
        <Card>
          <p className="text-sm text-surface-500 text-center py-8">
            No exchange connections configured. Connect an exchange to start trading.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exchangeList.map((ex: Record<string, unknown>) => (
            <Card key={String(ex.id)}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-surface-100">{String(ex.name)}</h3>
                <div className="flex items-center gap-2">
                  <Badge variant={ex.is_connected ? 'profit' : 'loss'}>
                    {ex.is_connected ? 'Connected' : 'Disconnected'}
                  </Badge>
                  {ex.is_live_enabled ? <Badge variant="warning">LIVE</Badge> : null}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-surface-500">Type:</span>{' '}
                  <span className="text-surface-300 capitalize">{String(ex.exchange_type)}</span>
                </div>
                <div>
                  <span className="text-surface-500">Last Connected:</span>{' '}
                  <span className="text-surface-300">
                    {ex.last_connected ? formatDateTime(String(ex.last_connected)) : 'Never'}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Connect Modal */}
      <Modal isOpen={showConnect} onClose={() => setShowConnect(false)} title="Connect Exchange">
        <form onSubmit={handleConnect} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Connection Name</label>
            <input className="input" placeholder="My Binance Account" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Exchange</label>
            <select className="select" value={form.exchange_type} onChange={(e) => setForm({ ...form, exchange_type: e.target.value })}>
              <option value="binance">Binance</option>
              <option value="kraken">Kraken</option>
              <option value="coinbase">Coinbase</option>
              <option value="bybit">Bybit</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">API Key</label>
            <input className="input font-mono" placeholder="API Key" value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">API Secret</label>
            <input className="input font-mono" type="password" placeholder="API Secret" value={form.api_secret}
              onChange={(e) => setForm({ ...form, api_secret: e.target.value })} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Passphrase (optional, for Coinbase)</label>
            <input className="input" placeholder="Passphrase" value={form.passphrase}
              onChange={(e) => setForm({ ...form, passphrase: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowConnect(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={connectExchange.isPending}>
              {connectExchange.isPending ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Live Activation Modal */}
      <Modal isOpen={showLiveActivation} onClose={() => setShowLiveActivation(false)} title="Activate Live Trading" size="lg">
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-loss/10 border border-loss/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-loss flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-loss">WARNING: LIVE TRADING</p>
                <p className="text-xs text-surface-400 mt-1">
                  Activating live trading will use REAL funds. All trades will be executed on the connected exchange
                  with real money at risk. You can lose your entire investment. Make sure you understand all risks
                  before proceeding.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleActivateLive} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Exchange Connection ID</label>
              <input className="input" placeholder="Exchange UUID" value={liveForm.exchange_id}
                onChange={(e) => setLiveForm({ ...liveForm, exchange_id: e.target.value })} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">
                Type: I UNDERSTAND THE RISKS OF LIVE TRADING
              </label>
              <input className="input font-mono" placeholder="Type the exact phrase above"
                value={liveForm.confirmation_phrase}
                onChange={(e) => setLiveForm({ ...liveForm, confirmation_phrase: e.target.value })} required />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={liveForm.risk_acknowledgement}
                onChange={(e) => setLiveForm({ ...liveForm, risk_acknowledgement: e.target.checked })}
                className="mt-0.5 rounded border-surface-600 bg-surface-800 text-brand-600 focus:ring-brand-500" />
              <span className="text-xs text-surface-400">
                I acknowledge that live trading involves real financial risk, that losses can exceed my initial investment,
                and that I am solely responsible for all trading decisions and outcomes.
              </span>
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowLiveActivation(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-danger"
                disabled={activateLive.isPending || !liveForm.risk_acknowledgement || !liveForm.confirmation_phrase}>
                {activateLive.isPending ? 'Activating...' : 'Activate Live Trading'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Emergency Shutdown Modal */}
      <Modal isOpen={showShutdown} onClose={() => setShowShutdown(false)} title="Emergency Shutdown">
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
            <p className="text-sm font-medium text-warning">This will block ALL trading activity immediately.</p>
            <p className="text-xs text-surface-400 mt-1">
              Emergency shutdown will prevent any new trades from being placed and cancel pending orders.
              This is a safety mechanism for extreme market conditions or suspected system issues.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Reason for shutdown</label>
            <textarea className="input min-h-[80px]" placeholder="Describe why you're activating emergency shutdown"
              value={shutdownReason} onChange={(e) => setShutdownReason(e.target.value)} />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowShutdown(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleShutdown} className="btn-danger" disabled={emergencyShutdown.isPending}>
              <Shield className="w-4 h-4" /> {emergencyShutdown.isPending ? 'Activating...' : 'Activate Emergency Shutdown'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
